/**
 * Visual theme resolution and application.
 *
 * The SPA supports normal light/dark visual modes selected via the `theme`
 * query parameter (e.g. `?theme=dark`). The theme only affects presentation:
 * it is applied as a `data-theme` attribute on the document root and never
 * touches form state or the submitted payload.
 *
 * Fallback rules (WI-TV-W1-005):
 * - Missing or unknown theme falls back to `light`.
 * - Light mode preserves the current/default visual mode.
 * - High-viz values (`light-highviz`, `dark-highviz`) are not yet implemented
 *   and fall back to their closest supported base mode (`light` / `dark`)
 *   until `WI-TV-W1-009`. They must not fail.
 */

/** The visual themes this WI actually renders. */
export type Theme = "light" | "dark";

export const DEFAULT_THEME: Theme = "light";

/** Query parameter name used to select the theme. */
export const THEME_QUERY_PARAM = "theme";

/**
 * Maps a raw, untrusted `theme` value to a supported {@link Theme}.
 *
 * Matching is case-insensitive and whitespace-tolerant. High-viz variants fall
 * back to their closest supported base mode until high-visibility support lands
 * in WI-TV-W1-009. Anything unrecognized (including `null`/`undefined`/empty)
 * falls back to {@link DEFAULT_THEME}.
 */
export function resolveTheme(raw: string | null | undefined): Theme {
  if (typeof raw !== "string") return DEFAULT_THEME;
  const value = raw.trim().toLowerCase();

  switch (value) {
    case "light":
      return "light";
    case "dark":
      return "dark";
    // High-viz variants are not implemented yet (WI-TV-W1-009); fall back to
    // the closest supported base mode instead of failing.
    case "light-highviz":
      return "light";
    case "dark-highviz":
      return "dark";
    default:
      return DEFAULT_THEME;
  }
}

/**
 * Reads and resolves the theme from a URL search string (e.g.
 * `"?theme=dark"`). Missing/unknown values resolve to {@link DEFAULT_THEME}.
 */
export function resolveThemeFromSearch(search: string): Theme {
  const params = new URLSearchParams(search);
  return resolveTheme(params.get(THEME_QUERY_PARAM));
}

/**
 * Applies a resolved theme by setting `data-theme` on the document root. Styling
 * keys off this attribute, so the whole page, form, error, and completion
 * surfaces switch together. Works inside an iframe with no parent-page support.
 */
export function applyTheme(theme: Theme, doc: Document = document): void {
  doc.documentElement.setAttribute("data-theme", theme);
}

/**
 * Resolves the theme from the current location's query string and applies it.
 * Returns the resolved theme for convenience/testing.
 */
export function initTheme(win: Window = window): Theme {
  const theme = resolveThemeFromSearch(win.location.search);
  applyTheme(theme, win.document);
  return theme;
}
